import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../components/Icon';
import { CardGrid } from '../components/ProfileCard';
import { ChipSelect, EmptyState, Page, PageHead, Select, SkeletonGrid } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api, toQuery } from '../utils/api';
import { HEIGHTS } from '../utils/format';

const AGES = Array.from({ length: 40 }, (_, i) => String(18 + i));

function Group({ title, children, defaultOpen = false, count }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fgroup">
      <button className="fgroup__head" onClick={() => setOpen(!open)}>
        <span>{title}{count ? <span className="tabs__count">{count}</span> : null}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }}><Icon name="chevronDown" size={16} /></motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div className="fgroup__body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <div className="fgroup__pad">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const fromPrefs = (p = {}) => ({
  ageMin: String(p.minAge || ''),
  ageMax: String(p.maxAge || ''),
  heightMin: p.minHeightCm || '',
  heightMax: p.maxHeightCm || '',
  religion: p.religions || [],
  motherTongue: p.motherTongues || [],
  city: p.cities || [],
  maritalStatus: p.maritalStatus || [],
  qualification: p.qualifications || [],
  profession: p.professions || [],
  diet: p.diets || [],
});
const EMPTY = { ...fromPrefs(), withPhoto: false, verified: false, online: false, text: '', sort: 'relevance' };

export default function Search() {
  const { user, meta } = useApp();
  const o = meta?.options || {};
  const [f, setF] = useState(() => ({ ...EMPTY, ...fromPrefs(user.partnerPrefs) }));
  const [res, setRes] = useState(null);
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState(false);
  const timer = useRef();

  const set = (k) => (v) => {
    setF((s) => ({ ...s, [k]: v }));
    setPage(1);
  };

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api(`/profiles/search?${toQuery({ ...f, page })}`)
        .then((d) => setRes((prev) => (page > 1 && prev ? { ...d, items: [...prev.items, ...d.items] } : d)))
        .catch(() => setRes({ items: [], total: 0 }));
    }, 350);
    return () => clearTimeout(timer.current);
  }, [f, page]);

  const activeCount = ['religion', 'motherTongue', 'city', 'maritalStatus', 'qualification', 'profession', 'diet'].reduce((s, k) => s + f[k].length, 0) + ['withPhoto', 'verified', 'online'].filter((k) => f[k]).length;

  const filters = (
    <div className="filters">
      <div className="filters__top">
        <strong><Icon name="filter" size={16} /> Filters {activeCount > 0 && <span className="tabs__count">{activeCount}</span>}</strong>
        <div className="filters__links">
          <button className="link-btn" onClick={() => { setF({ ...EMPTY, ...fromPrefs(user.partnerPrefs) }); setPage(1); }}>My preferences</button>
          <button className="link-btn" onClick={() => { setF(EMPTY); setPage(1); }}>Clear all</button>
        </div>
      </div>
      <Group title="Age & height" defaultOpen>
        <div className="range-row">
          <Select value={f.ageMin} onChange={set('ageMin')} options={AGES} placeholder="Min age" />
          <span>to</span>
          <Select value={f.ageMax} onChange={set('ageMax')} options={AGES} placeholder="Max age" />
        </div>
        <div className="range-row">
          <Select value={f.heightMin} onChange={set('heightMin')} options={HEIGHTS.map((h) => ({ value: h.cm, label: h.label }))} placeholder="Min height" />
          <span>to</span>
          <Select value={f.heightMax} onChange={set('heightMax')} options={HEIGHTS.map((h) => ({ value: h.cm, label: h.label }))} placeholder="Max height" />
        </div>
      </Group>
      <Group title="Quick toggles" defaultOpen>
        {[['withPhoto', 'With photo', 'image'], ['verified', 'Verified only', 'shield'], ['online', 'Online now', 'sparkle']].map(([k, l, ic]) => (
          <label key={k} className="switch-row">
            <span><Icon name={ic} size={15} /> {l}</span>
            <input type="checkbox" className="switch" checked={f[k]} onChange={(e) => set(k)(e.target.checked)} />
          </label>
        ))}
      </Group>
      <Group title="Religion" count={f.religion.length} defaultOpen={f.religion.length > 0}><ChipSelect options={o.RELIGIONS} value={f.religion} onChange={set('religion')} /></Group>
      <Group title="Mother tongue" count={f.motherTongue.length}><ChipSelect options={o.MOTHER_TONGUES} value={f.motherTongue} onChange={set('motherTongue')} /></Group>
      <Group title="City" count={f.city.length}><ChipSelect options={o.CITIES} value={f.city} onChange={set('city')} /></Group>
      <Group title="Profession" count={f.profession.length}><ChipSelect options={o.PROFESSIONS} value={f.profession} onChange={set('profession')} /></Group>
      <Group title="Qualification" count={f.qualification.length}><ChipSelect options={o.QUALIFICATIONS} value={f.qualification} onChange={set('qualification')} /></Group>
      <Group title="Marital status" count={f.maritalStatus.length}><ChipSelect options={o.MARITAL} value={f.maritalStatus} onChange={set('maritalStatus')} /></Group>
      <Group title="Diet" count={f.diet.length}><ChipSelect options={o.DIETS} value={f.diet} onChange={set('diet')} /></Group>
    </div>
  );

  return (
    <Page>
      <div className="container">
        <PageHead eyebrow="Search" title="Find someone specific" sub="Search by name, profile ID, specialty or hospital — then narrow down with filters." />
        <div className="searchbar">
          <Icon name="search" size={20} />
          <input value={f.text} onChange={(e) => set('text')(e.target.value)} placeholder="Try “Cardiology”, “AIIMS” or “CTS1004”" />
          <Select value={f.sort} onChange={set('sort')} options={[{ value: 'relevance', label: 'Best match' }, { value: 'active', label: 'Recently active' }, { value: 'newest', label: 'Newest first' }, { value: 'age', label: 'Youngest first' }]} placeholder="Sort" />
          <button className="btn btn--ghost btn--sm only-mobile" onClick={() => setDrawer(true)}><Icon name="filter" size={16} /> {activeCount || ''}</button>
        </div>
        <div className="search-layout">
          <aside className="search-layout__side only-desktop">{filters}</aside>
          <section>
            {!res ? (
              <SkeletonGrid />
            ) : (
              <>
                <motion.p key={res.total} className="results-count" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}>
                  <strong>{res.total}</strong> {res.total === 1 ? 'profile matches' : 'profiles match'} your search
                </motion.p>
                {res.items.length ? (
                  <>
                    <CardGrid key={JSON.stringify(f)} items={res.items} />
                    {res.page < res.pages && (
                      <div className="center">
                        <button className="btn btn--ghost" onClick={() => setPage(page + 1)}>Show more profiles</button>
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyState icon="search" title="No exact matches" text="Try removing a filter or widening the age range." action={<button className="btn btn--primary" onClick={() => setF(EMPTY)}>Clear filters</button>} />
                )}
              </>
            )}
          </section>
        </div>
      </div>
      <AnimatePresence>
        {drawer && (
          <motion.div className="drawer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)}>
            <motion.div className="drawer__panel" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 300 }} onClick={(e) => e.stopPropagation()}>
              {filters}
              <button className="btn btn--primary btn--block" onClick={() => setDrawer(false)}>Show {res?.total ?? ''} profiles</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Page>
  );
}
